import { DEFAULT_ACCENT_COLOR, isAccentColor, type AccentColor } from './accentColor';

/**
 * The DOM side of accent colour — mirrors `applyAppearance.ts` exactly, for
 * the same reason: IndexedDB is the system of record, but it's asynchronous,
 * so the chosen colour is mirrored into localStorage (synchronous, readable
 * by index.html's pre-paint script) to avoid a flash of red before the real
 * value loads. This is a cache, never the source of truth.
 */

export const ACCENT_COLOR_STORAGE_KEY = 'liftx.accentColor';

/** Read the mirrored colour. Returns the default when absent or corrupt. */
export function readMirroredAccentColor(): AccentColor {
  try {
    const raw = localStorage.getItem(ACCENT_COLOR_STORAGE_KEY);
    return isAccentColor(raw) ? raw : DEFAULT_ACCENT_COLOR;
  } catch {
    // Safari throws on storage access in some privacy configurations.
    return DEFAULT_ACCENT_COLOR;
  }
}

export function writeMirroredAccentColor(color: AccentColor): void {
  try {
    localStorage.setItem(ACCENT_COLOR_STORAGE_KEY, color);
  } catch {
    // A failed mirror costs a flash of the wrong colour, never correctness.
  }
}

/**
 * Apply an accent colour to the document. `tokens.css` keys every non-red
 * colour off `[data-accent='...']`; red has no override block, so leaving the
 * attribute set to `'red'` — or reading `:root` before this ever runs — both
 * resolve to the same, unmodified default tokens.
 */
export function applyAccentColor(color: AccentColor): AccentColor {
  document.documentElement.setAttribute('data-accent', color);
  return color;
}
